import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Mail, Lock, LogIn, Phone, MessageCircle, MapPin, Globe, Instagram, Twitter, Linkedin, Facebook, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/auth";

const SOCIAL_META = [
  { key: "website", icon: Globe, label: "Website" },
  { key: "instagram", icon: Instagram, label: "Instagram" },
  { key: "twitter", icon: Twitter, label: "Twitter" },
  { key: "linkedin", icon: Linkedin, label: "LinkedIn" },
  { key: "facebook", icon: Facebook, label: "Facebook" },
];

export default function TenantLogin() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [agency, setAgency] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/portal/agency/${subdomain}`);
        setAgency(data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [subdomain]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post(`/portal/agency/${subdomain}/login`, { email: email.trim(), password });
      nav("/portal");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center hero-gradient p-6">
        <div className="bento p-10 max-w-md text-center" data-testid="tenant-login-not-found">
          <img src="/logo-mark.png" alt="Foliosy" className="w-12 h-12 object-contain mx-auto mb-4 opacity-60" />
          <h1 className="text-2xl font-bold tracking-tight mb-2">Portal not found</h1>
          <p className="text-slate-600 mb-6">The agency portal you're looking for doesn't exist or is unavailable.</p>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 underline">Back to Foliosy</Link>
        </div>
      </div>
    );
  }

  if (!agency) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  }

  const color = agency.branding?.primary_color || "#0F172A";
  const logo = agency.branding?.logo_url;
  const pp = agency.public_page || {};
  const socials = pp.socials || {};
  const visibleSocials = SOCIAL_META.filter((s) => socials[s.key]);
  const portfolio = pp.portfolio_items || [];
  const testimonials = pp.testimonials || [];

  const heroTitle = pp.hero_title || `Welcome to ${agency.name}`;
  const heroDesc = pp.hero_description;
  const heroImage = pp.hero_image_url;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ "--tenant-primary": color }} data-testid="tenant-login">
      {/* Top nav */}
      <header className="px-6 md:px-12 py-5 flex items-center justify-between border-b border-slate-200/60 bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="flex items-center gap-3">
          {logo ? (
            <img src={logo} alt={agency.name} className="h-8 object-contain" />
          ) : (
            <div className="w-9 h-9 rounded-xl text-white flex items-center justify-center font-bold" style={{ backgroundColor: color }}>
              {agency.name[0]}
            </div>
          )}
          <span className="font-bold tracking-tight" data-testid="tenant-login-agency-name">{agency.name}</span>
        </div>
        <a href="#login" className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
          Tenant sign in <ArrowRight className="w-3 h-3" />
        </a>
      </header>

      {/* HERO */}
      <section className="px-6 md:px-12 pt-16 pb-20 max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center" data-testid="public-hero">
        <div>
          <p className="eyebrow mb-3">Studio</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            {heroTitle}
          </h1>
          {heroDesc && (
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
              {heroDesc}
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#login"
              className="inline-flex items-center gap-2 text-white rounded-full px-7 py-3.5 font-medium hover:opacity-90 active:scale-95"
              style={{ backgroundColor: color }}
              data-testid="hero-cta-login"
            >
              <LogIn className="w-4 h-4" /> Sign in to your portal
            </a>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium border border-slate-200 hover:bg-slate-100 active:scale-95"
              data-testid="hero-cta-contact"
            >
              Get in touch
            </a>
          </div>
        </div>
        <div>
          {heroImage ? (
            <img
              src={heroImage}
              alt={agency.name}
              className="w-full aspect-[4/3] object-cover rounded-3xl shadow-xl"
              data-testid="hero-image"
            />
          ) : (
            <div
              className="w-full aspect-[4/3] rounded-3xl grain-overlay flex items-center justify-center text-white text-7xl font-bold"
              style={{ backgroundColor: color }}
              data-testid="hero-image-placeholder"
            >
              {agency.name[0]}
            </div>
          )}
        </div>
      </section>

      {/* CONTACT + LOGIN */}
      <section id="contact" className="px-6 md:px-12 pb-24 max-w-6xl mx-auto grid lg:grid-cols-2 gap-10" data-testid="contact-login-section">
        {/* Left — contact */}
        <div className="bento p-10" data-testid="contact-card">
          <p className="eyebrow mb-3">Contact</p>
          <h2 className="text-3xl font-bold tracking-tight mb-6">Let's talk.</h2>
          <div className="space-y-4 text-slate-700">
            {pp.contact_email && (
              <a
                href={`mailto:${pp.contact_email}`}
                className="flex items-center gap-3 hover:text-slate-900 group"
                data-testid="contact-email"
              >
                <span className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-slate-900 group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
                  <Mail className="w-4 h-4" />
                </span>
                <span className="font-medium">{pp.contact_email}</span>
              </a>
            )}
            {pp.contact_phone && (
              <a
                href={`tel:${pp.contact_phone.replace(/\s+/g, "")}`}
                className="flex items-center gap-3 hover:text-slate-900 group"
                data-testid="contact-phone"
              >
                <span className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-slate-900 group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
                  <Phone className="w-4 h-4" />
                </span>
                <span className="font-medium">{pp.contact_phone}</span>
              </a>
            )}
            {pp.contact_whatsapp && (
              <a
                href={`https://wa.me/${pp.contact_whatsapp.replace(/[^\d+]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 hover:text-slate-900 group"
                data-testid="contact-whatsapp"
              >
                <span className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-[#25D366] group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
                  <MessageCircle className="w-4 h-4" />
                </span>
                <span className="font-medium">WhatsApp · {pp.contact_whatsapp}</span>
              </a>
            )}
            {pp.contact_address && (
              <div className="flex items-start gap-3" data-testid="contact-address">
                <span className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </span>
                <span className="font-medium pt-2">{pp.contact_address}</span>
              </div>
            )}
            {!pp.contact_email && !pp.contact_phone && !pp.contact_whatsapp && !pp.contact_address && (
              <p className="text-slate-400 text-sm" data-testid="no-contact-info">
                {agency.name} hasn't added contact info yet.
              </p>
            )}
          </div>

          {visibleSocials.length > 0 && (
            <div className="mt-8 pt-6 border-t border-slate-200 flex flex-wrap gap-2" data-testid="contact-socials">
              {visibleSocials.map((s) => (
                <a
                  key={s.key}
                  href={socials[s.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-900 hover:text-white flex items-center justify-center transition-colors"
                  aria-label={s.label}
                  data-testid={`social-${s.key}`}
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Right — login */}
        <div id="login" className="bento p-10" data-testid="login-card">
          <p className="eyebrow mb-3">Tenant portal</p>
          <h2 className="text-3xl font-bold tracking-tight mb-2">Sign in</h2>
          <p className="text-slate-600 text-sm mb-6">
            Use the credentials {agency.name} shared with you.
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="tenant-login-form">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
                  data-testid="tenant-login-email"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  required
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-3 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
                  data-testid="tenant-login-password"
                />
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-500" data-testid="tenant-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full text-white rounded-full py-3 font-medium hover:opacity-90 active:scale-95 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: color }}
              data-testid="tenant-login-submit"
            >
              <LogIn className="w-4 h-4" />
              {submitting ? "Signing in…" : "Sign in"}
            </button>
            <p className="text-xs text-slate-500 text-center pt-2">
              Forgot your password? Ask {agency.name} to reset it for you.
            </p>
          </form>
        </div>
      </section>

      {/* PORTFOLIO */}
      {portfolio.length > 0 && (
        <section className="px-6 md:px-12 py-20 max-w-6xl mx-auto" data-testid="public-portfolio">
          <div className="mb-10">
            <p className="eyebrow mb-3">Portfolio</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Recent work.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolio.map((p) => (
              <a
                key={p.id}
                href={p.link || "#"}
                target={p.link ? "_blank" : undefined}
                rel={p.link ? "noopener noreferrer" : undefined}
                className="group block bento overflow-hidden p-0 hover:shadow-lg transition-shadow"
                data-testid={`portfolio-card-${p.id}`}
              >
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center text-white text-4xl font-bold"
                      style={{ backgroundColor: color }}
                    >
                      {(p.name || "·")[0]}
                    </div>
                  )}
                </div>
                <div className="px-5 py-4 flex items-center justify-between gap-2">
                  <span className="font-semibold tracking-tight text-slate-900 truncate">{p.name || "Untitled project"}</span>
                  {p.link && (
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition" />
                  )}
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="px-6 md:px-12 py-20 bg-white border-y border-slate-200" data-testid="public-testimonials">
          <div className="max-w-6xl mx-auto">
            <div className="mb-10">
              <p className="eyebrow mb-3">Testimonials</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Trusted by clients.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <figure
                  key={t.id}
                  className="bento p-7 flex flex-col gap-5"
                  data-testid={`testimonial-card-${t.id}`}
                >
                  <blockquote className="text-slate-700 leading-relaxed flex-1">
                    <span className="text-4xl leading-none align-top mr-1" style={{ color }}>"</span>
                    {t.quote}
                  </blockquote>
                  <figcaption className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                      {t.image_url ? (
                        <img
                          src={t.image_url}
                          alt={t.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <span className="font-bold text-slate-500">{(t.name || "·")[0]}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{t.name}</p>
                      {t.title && <p className="text-xs text-slate-500 truncate">{t.title}</p>}
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="px-6 md:px-12 py-8 border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-xs text-slate-400">
          <span>© {new Date().getFullYear()} {agency.name}</span>
          <a href="https://foliosy.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">
            Powered by Foliosy
          </a>
        </div>
      </footer>
    </div>
  );
}
